import React from 'react';
import type { EdgeConditionBuilderProps } from 'interfaces/workflows/EdgeConditionBuilderProps';
import type {
  ConditionFieldDefinition,
  ConditionFieldType,
  ConditionOperator,
  ConditionRule,
} from 'models/workflows';
import { isLeafRule } from 'models/workflows';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  eq: '=',
  neq: '≠',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
};

function getOperatorsForType(type: ConditionFieldType): ConditionOperator[] {
  if (type === 'number') return ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'];
  return ['eq', 'neq'];
}

function makeBlankLeaf(availableFields: ConditionFieldDefinition[]): ConditionRule {
  const field = availableFields[0];
  return {
    field: field.key,
    operator: 'eq',
    value: field.type === 'enum' && field.enumValues?.length ? field.enumValues[0] : '',
  };
}

// ─── Internal component (carries an extra `isNested` flag for Remove button) ──

interface InternalProps extends EdgeConditionBuilderProps {
  isNested?: boolean;
}

const EdgeConditionBuilderCore: React.FC<InternalProps> = ({
  availableFields,
  value,
  onChange,
  isNested = false,
}) => {
  // ── Empty fields state ────────────────────────────────────────────────────
  if (availableFields.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic">
        The selected node produces no addressable fields. Conditions cannot be defined on this edge.
      </p>
    );
  }

  // ── No condition yet ──────────────────────────────────────────────────────
  if (value === null) {
    return (
      <button
        type="button"
        onClick={() => onChange(makeBlankLeaf(availableFields))}
        className="inline-flex items-center justify-center rounded-lg border border-dashed border-blue-400 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        + Add Condition
      </button>
    );
  }

  // ── Leaf rule (single comparison row) ─────────────────────────────────────
  if (isLeafRule(value)) {
    const fieldDef = availableFields.find((f) => f.key === value.field) ?? availableFields[0];
    const operators = getOperatorsForType(fieldDef.type);

    const handleFieldChange = (newKey: string) => {
      const newFieldDef = availableFields.find((f) => f.key === newKey) ?? availableFields[0];
      const newOperators = getOperatorsForType(newFieldDef.type);
      const initialValue =
        newFieldDef.type === 'enum' && newFieldDef.enumValues?.length
          ? newFieldDef.enumValues[0]
          : '';
      onChange({ ...value, field: newKey, operator: newOperators[0], value: initialValue });
    };

    const handleOperatorChange = (op: string) => {
      onChange({ ...value, operator: op as ConditionOperator });
    };

    const handleValueChange = (raw: string) => {
      const coerced: string | number =
        fieldDef.type === 'number' && raw !== '' ? Number(raw) : raw;
      onChange({ ...value, value: coerced });
    };

    const handlePromote = (type: 'and' | 'or') => {
      onChange({ [type]: [value, makeBlankLeaf(availableFields)] } as ConditionRule);
    };

    return (
      <div className="flex flex-wrap items-center gap-2">
        {/* Field dropdown */}
        <select
          aria-label="Condition field"
          value={value.field}
          onChange={(e) => handleFieldChange(e.target.value)}
          className="rounded border border-gray-300 py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          {availableFields.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>

        {/* Operator dropdown */}
        <select
          aria-label="Condition operator"
          value={value.operator}
          onChange={(e) => handleOperatorChange(e.target.value)}
          className="rounded border border-gray-300 py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          {operators.map((op) => (
            <option key={op} value={op}>
              {OPERATOR_LABELS[op]}
            </option>
          ))}
        </select>

        {/* Value input — varies by field type */}
        {fieldDef.type === 'enum' ? (
          <select
            aria-label="Condition value"
            value={String(value.value ?? '')}
            onChange={(e) => handleValueChange(e.target.value)}
            className="rounded border border-gray-300 py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            {(fieldDef.enumValues ?? []).map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        ) : fieldDef.type === 'number' ? (
          <input
            type="number"
            aria-label="Condition value"
            value={String(value.value ?? '')}
            onChange={(e) => handleValueChange(e.target.value)}
            className="w-24 rounded border border-gray-300 py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        ) : (
          <input
            type="text"
            aria-label="Condition value"
            value={String(value.value ?? '')}
            onChange={(e) => handleValueChange(e.target.value)}
            className="rounded border border-gray-300 py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        )}

        {/* Promote to AND group */}
        <button
          type="button"
          onClick={() => handlePromote('and')}
          className="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
        >
          AND
        </button>

        {/* Promote to OR group */}
        <button
          type="button"
          onClick={() => handlePromote('or')}
          className="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
        >
          OR
        </button>

        {/* Remove — only shown when this row is a child inside a group */}
        {isNested && (
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label="Remove condition row"
            className="text-xs font-medium text-red-400 hover:text-red-600 focus:outline-none"
          >
            Remove
          </button>
        )}
      </div>
    );
  }

  // ── Logical group (AND / OR) ───────────────────────────────────────────────
  const groupType = value.and !== undefined ? 'and' : 'or';
  const children = (value.and ?? value.or) as ConditionRule[];

  const handleChildChange = (index: number, newChild: ConditionRule | null) => {
    if (newChild === null) {
      const remaining = children.filter((_, i) => i !== index);
      if (remaining.length === 0) {
        onChange(null);
      } else if (remaining.length === 1) {
        // Collapse the group back to the single remaining child
        onChange(remaining[0]);
      } else {
        onChange({ [groupType]: remaining } as ConditionRule);
      }
    } else {
      const updated = children.map((c, i) => (i === index ? newChild : c));
      onChange({ [groupType]: updated } as ConditionRule);
    }
  };

  const handleAddRow = () => {
    onChange({ [groupType]: [...children, makeBlankLeaf(availableFields)] } as ConditionRule);
  };

  return (
    <div className="border-l-2 border-blue-300 pl-3 space-y-2">
      {/* Group header */}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
          {groupType === 'and' ? 'AND' : 'OR'}
        </span>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs font-medium text-red-400 hover:text-red-600 focus:outline-none"
        >
          Clear All
        </button>
      </div>

      {/* Child rules — each rendered recursively */}
      {children.map((child, index) => (
        <EdgeConditionBuilderCore
          key={index}
          availableFields={availableFields}
          value={child}
          onChange={(newChild) => handleChildChange(index, newChild)}
          isNested
        />
      ))}

      {/* Add Row */}
      <button
        type="button"
        onClick={handleAddRow}
        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 focus:outline-none"
      >
        + Add Row
      </button>
    </div>
  );
};

// ─── Public component ─────────────────────────────────────────────────────────

const EdgeConditionBuilder: React.FC<EdgeConditionBuilderProps> = (props) => (
  <EdgeConditionBuilderCore {...props} />
);

export default EdgeConditionBuilder;
